<script setup lang="ts">
import { ref, onMounted, computed } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import { useI18n } from 'vue-i18n'
import eventService from '../services/event.service'
import categoryService from '../services/category.service'
import AttendanceReportModal from '../components/AttendanceReportModal.vue'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const events = ref<any[]>([])
const categories = ref<any[]>([])
const loading = ref(false)
const error = ref<string | null>(null)

// Date filters - default: -1 day to +14 days from today
const getDefaultDateFrom = () => {
  const date = new Date()
  date.setDate(date.getDate() - 1)
  return date.toISOString().split('T')[0]
}

const getDefaultDateTo = () => {
  const date = new Date()
  date.setDate(date.getDate() + 14)
  return date.toISOString().split('T')[0]
}

// Initialize from URL query params or use defaults
const getInitialDateFrom = () => {
  const queryDateFrom = route.query.date_from as string
  return queryDateFrom || getDefaultDateFrom()
}

const getInitialDateTo = () => {
  const queryDateTo = route.query.date_to as string
  return queryDateTo || getDefaultDateTo()
}

const dateFrom = ref(getInitialDateFrom())
const dateTo = ref(getInitialDateTo())
const displayDateFrom = ref('')
const displayDateTo = ref('')

// Category filter
const selectedCategories = ref<string[]>([])
const showCategoryDropdown = ref(false)

// Pagination
const currentPage = ref(1)
const perPage = ref(50)
const totalEvents = ref(0)
const lastPage = ref(1)
const fromItem = computed(() => totalEvents.value > 0 ? (currentPage.value - 1) * perPage.value + 1 : 0)
const toItem = computed(() => Math.min(currentPage.value * perPage.value, totalEvents.value))

// Delete modal
const showDeleteModal = ref(false)
const showAttendanceReportModal = ref(false)
const eventToDelete = ref<any>(null)
const deleting = ref(false)

// Dropdowns
const openDropdownId = ref<string | null>(null)

// Accordions (mobile)
const openAccordionIds = ref<Set<number>>(new Set())

// Get club UUID from init data
const clubUuid = computed(() => authStore.initData?.club?.uuid || null)

// Check permissions
const hasPermission = (permission: string) => {
  const permissions = authStore.initData?.permission_model || []
  return permissions.includes(permission)
}

const canCreate = computed(() => hasPermission('EVENT_CREATE'))
const canShow = computed(() => hasPermission('EVENT_SHOW'))
const canUpdate = computed(() => hasPermission('EVENT_UPDATE'))
const canDelete = computed(() => hasPermission('EVENT_DELETE'))
const canAttendanceStore = computed(() => hasPermission('EVENT_ATTENDANCE_STORE'))
const canAttendanceUpdate = computed(() => hasPermission('EVENT_ATTENDANCE_UPDATE'))
const canSeeActions = computed(() => canShow.value || canUpdate.value || canDelete.value || canAttendanceStore.value || canAttendanceUpdate.value)

// Get current role
const currentRole = computed(() => authStore.initData?.persons?.current?.role || null)

// Get possible categories for COACH
const possibleCategories = computed(() => authStore.initData?.possible_categories || [])

// Check if category is accessible (for COACH role)
const isCategoryAccessible = (categoryUuid: string) => {
  // OWNER and SUPERVISOR can access all categories
  if (currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR') {
    return true
  }
  // COACH checks possible_categories
  if (currentRole.value === 'COACH') {
    return possibleCategories.value.includes(categoryUuid)
  }
  // PLAYER cannot access categories
  return false
}

// Check if user can click on category (OWNER, SUPERVISOR, COACH can - but COACH only their categories)
const canClickCategory = computed(() => {
  return currentRole.value === 'OWNER' || currentRole.value === 'SUPERVISOR' || currentRole.value === 'COACH'
})

// Day abbreviations
const isEventPast = (event: any) => {
  const endStr: string = event.end
  if (!endStr) return false
  return new Date(endStr.replace(' ', 'T')) < new Date()
}

const getDayAbbreviation = (day: string) => {
  const days: Record<string, string> = {
    [t('events.days.monday')]: t('events.days.mondayShort'),
    [t('events.days.tuesday')]: t('events.days.tuesdayShort'),
    [t('events.days.wednesday')]: t('events.days.wednesdayShort'),
    [t('events.days.thursday')]: t('events.days.thursdayShort'),
    [t('events.days.friday')]: t('events.days.fridayShort'),
    [t('events.days.saturday')]: t('events.days.saturdayShort'),
    [t('events.days.sunday')]: t('events.days.sundayShort')
  }
  return days[day] || day
}

// Fetch categories (skip for PLAYER role)
const fetchCategories = async () => {
  if (!clubUuid.value || currentRole.value === 'PLAYER') return

  try {
    const categoriesData = await categoryService.getCategoriesForFilters(clubUuid.value)
    categories.value = categoriesData
  } catch (err: any) {
    console.error('Fetch categories error:', err)
  }
}

// Toggle category selection
const toggleCategory = (categoryUuid: string) => {
  const index = selectedCategories.value.indexOf(categoryUuid)
  if (index > -1) {
    selectedCategories.value.splice(index, 1)
  } else {
    selectedCategories.value.push(categoryUuid)
  }
  // Auto-fetch when category changes
  filterEvents()
}

// Remove category filter
const removeCategory = (categoryUuid: string) => {
  const index = selectedCategories.value.indexOf(categoryUuid)
  if (index > -1) {
    selectedCategories.value.splice(index, 1)
    filterEvents()
  }
}

// Get category name
const getCategoryName = (categoryUuid: string): string => {
  const category = categories.value.find((cat: any) => cat.uuid === categoryUuid)
  return category?.name || categoryUuid
}

// Fetch events
const fetchEvents = async (page: number = 1) => {
  if (!clubUuid.value) {
    error.value = t('events.errorNoClub')
    return
  }

  loading.value = true
  error.value = null

  try {
    const response = await eventService.getEvents(
      clubUuid.value,
      dateFrom.value || undefined,
      dateTo.value || undefined,
      perPage.value,
      page,
      selectedCategories.value.length > 0 ? selectedCategories.value : undefined
    )
    events.value = response.data || []
    totalEvents.value = response.total || 0
    displayDateFrom.value = response.date_from || ''
    displayDateTo.value = response.date_to || ''

    // Calculate pagination
    lastPage.value = Math.ceil(totalEvents.value / perPage.value) || 1
    currentPage.value = page
  } catch (err: any) {
    console.error('Error fetching events:', err)
    error.value = err.response?.data?.message || t('events.errorFetchingEvents')
  } finally {
    loading.value = false
  }
}

// Filter events
const filterEvents = () => {
  currentPage.value = 1

  if (dateFrom.value && dateTo.value) {
    const from = new Date(dateFrom.value)
    const to = new Date(dateTo.value)
    const diffDays = Math.round((to.getTime() - from.getTime()) / (1000 * 60 * 60 * 24))
    if (diffDays > 90) {
      const capped = new Date(from)
      capped.setDate(capped.getDate() + 90)
      dateTo.value = capped.toISOString().slice(0, 10)
      toastStore.warning(t('events.dateRangeTooLarge'))
    }
  }

  // Update URL with date params
  const query: Record<string, string> = {}
  if (dateFrom.value) {
    query.date_from = dateFrom.value
  }
  if (dateTo.value) {
    query.date_to = dateTo.value
  }

  router.push({ path: '/events', query })

  fetchEvents(1)
}

// Pagination handlers
const changePage = (page: number) => {
  currentPage.value = page
  fetchEvents(page)
}

const prevPage = () => {
  if (currentPage.value > 1) {
    changePage(currentPage.value - 1)
  }
}

const nextPage = () => {
  if (currentPage.value < lastPage.value) {
    changePage(currentPage.value + 1)
  }
}

const changePerPage = () => {
  currentPage.value = 1
  fetchEvents(1)
}

// Calculate visible page numbers
const visiblePages = computed(() => {
  const maxVisible = 5
  const pages: number[] = []

  let start = Math.max(1, currentPage.value - Math.floor(maxVisible / 2))
  let end = Math.min(lastPage.value, start + maxVisible - 1)

  if (end - start + 1 < maxVisible) {
    start = Math.max(1, end - maxVisible + 1)
  }

  for (let i = start; i <= end; i++) {
    pages.push(i)
  }

  return pages
})

// Delete handlers
const openDeleteModalFn = (event: any) => {
  eventToDelete.value = event
  showDeleteModal.value = true
}

const closeDeleteModal = () => {
  showDeleteModal.value = false
  eventToDelete.value = null
}

const handleDelete = async () => {
  if (!eventToDelete.value || !clubUuid.value) return

  deleting.value = true

  try {
    // Extract date from start datetime
    const eventDate = eventToDelete.value.start?.split(' ')[0]
    const response = await eventService.deleteEvent(clubUuid.value, eventToDelete.value.uuid, eventDate)
    const message = response?.message || t('events.eventDeleted')
    toastStore.success(message)

    closeDeleteModal()
    fetchEvents(currentPage.value)
  } catch (err: any) {
    console.error('Error deleting event:', err)
    const errorMessage = err.response?.data?.message || t('events.errorDeletingEvent')
    toastStore.error(errorMessage)
  } finally {
    deleting.value = false
  }
}

// Dropdown toggle
const toggleDropdown = (dropdownId: string, event: Event) => {
  event.stopPropagation()

  if (openDropdownId.value === dropdownId) {
    openDropdownId.value = null
  } else {
    openDropdownId.value = dropdownId
  }
}

const closeDropdowns = () => {
  openDropdownId.value = null
}

// Check if event is in last three (for dropdown positioning)
const isLastThree = (index: number) => {
  return index >= events.value.length - 3
}

// Toggle accordion for mobile
const toggleAccordion = (index: number) => {
  if (openAccordionIds.value.has(index)) {
    openAccordionIds.value.delete(index)
  } else {
    openAccordionIds.value.add(index)
  }
}

// Close all dropdowns
const closeAllDropdowns = () => {
  openDropdownId.value = null
  showCategoryDropdown.value = false
}

// Close dropdowns when clicking outside
if (typeof window !== 'undefined') {
  document.addEventListener('click', closeDropdowns)
}

onMounted(() => {
  fetchCategories()
  fetchEvents()
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div class="w-full px-4 py-4 md:py-4 py-2">
      <!-- Page Header -->
      <div class="mb-4 md:mb-4 mb-2">
        <h1 class="text-2xl font-bold text-gray-900">
          {{ t('events.title') }} <span v-if="displayDateFrom && displayDateTo" class="text-sm font-normal text-gray-600">({{ t('events.inDays') }} {{ displayDateFrom }} - {{ displayDateTo }})</span>
        </h1>
      </div>

      <!-- Filters and Actions -->
      <div class="mb-4 flex flex-wrap justify-between items-start gap-4">
        <!-- Left side: Filters (above 500px) -->
        <div class="hidden min-[500px]:flex flex-wrap items-center gap-2">
          <!-- Category Filter Dropdown (hidden for PLAYER) -->
          <div v-if="currentRole !== 'PLAYER'" class="relative inline-block">
            <button
              @click.stop="showCategoryDropdown = !showCategoryDropdown"
              class="px-3 py-1.5 bg-white text-sm font-medium text-gray-700 rounded hover:bg-gray-50 border-2 border-gray-300"
            >
              {{ t('events.selectCategories') }}
              <svg class="inline-block w-3.5 h-3.5 ml-1" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div
              v-show="showCategoryDropdown"
              class="absolute left-0 mt-2 w-48 bg-white border-2 border-gray-300 rounded shadow-md z-50"
              @click.stop
            >
              <div class="p-2">
                <label
                  v-for="category in categories"
                  :key="category.uuid"
                  class="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :value="category.uuid"
                    :checked="selectedCategories.includes(category.uuid)"
                    @change="toggleCategory(category.uuid)"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  >
                  <span class="text-sm text-gray-700">{{ category.name }}</span>
                </label>
              </div>
            </div>
          </div>

          <label class="text-sm md:text-sm text-xs text-gray-700">{{ t('events.from') }}</label>
          <input type="date" v-model="dateFrom" class="px-3 py-1.5 md:px-3 md:py-1.5 px-2 py-1 text-sm md:text-sm text-xs border-2 border-gray-300 rounded">
          <label class="text-sm md:text-sm text-xs text-gray-700">{{ t('events.to') }}</label>
          <input type="date" v-model="dateTo" class="px-3 py-1.5 md:px-3 md:py-1.5 px-2 py-1 text-sm md:text-sm text-xs border-2 border-gray-300 rounded">
          <button @click="filterEvents" class="px-4 py-1.5 md:px-4 md:py-1.5 px-2 py-1 bg-blue-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-blue-700">
            {{ t('events.filter') }}
          </button>
        </div>

        <!-- Left side: Filters (below 500px - vertical layout) -->
        <div class="flex min-[500px]:hidden flex-col gap-2">
          <!-- Category Filter Dropdown (hidden for PLAYER) -->
          <div v-if="currentRole !== 'PLAYER'" class="relative inline-block">
            <button
              @click.stop="showCategoryDropdown = !showCategoryDropdown"
              class="px-2 py-1 bg-white text-xs font-medium text-gray-700 rounded hover:bg-gray-50 border-2 border-gray-300 w-full text-left"
            >
              {{ t('events.selectCategories') }}
              <svg class="inline-block w-3 h-3 ml-1 float-right mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </button>
            <div
              v-show="showCategoryDropdown"
              class="absolute left-0 mt-2 w-full bg-white border-2 border-gray-300 rounded shadow-md z-50"
              @click.stop
            >
              <div class="p-2">
                <label
                  v-for="category in categories"
                  :key="category.uuid"
                  class="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-100 rounded cursor-pointer"
                >
                  <input
                    type="checkbox"
                    :value="category.uuid"
                    :checked="selectedCategories.includes(category.uuid)"
                    @change="toggleCategory(category.uuid)"
                    class="w-4 h-4 text-blue-600 border-gray-300 rounded"
                  >
                  <span class="text-xs text-gray-700">{{ category.name }}</span>
                </label>
              </div>
            </div>
          </div>

          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-700">{{ t('events.from') }}</label>
            <input type="date" v-model="dateFrom" class="px-2 py-1 text-xs border-2 border-gray-300 rounded flex-1">
          </div>
          <div class="flex items-center gap-2">
            <label class="text-xs text-gray-700">{{ t('events.to') }}</label>
            <input type="date" v-model="dateTo" class="px-2 py-1 text-xs border-2 border-gray-300 rounded flex-1">
          </div>
          <button @click="filterEvents" class="px-2 py-1 bg-blue-600 text-xs font-medium text-white rounded hover:bg-blue-700">
            {{ t('events.filter') }}
          </button>
        </div>

        <!-- Right side: Add event and cycles link (hidden for PLAYER) -->
        <div v-if="currentRole !== 'PLAYER'" class="flex flex-col items-end gap-2 ml-auto">
          <router-link
            v-if="canCreate"
            to="/events/create"
            class="px-4 py-2 md:px-4 md:py-2 px-2 py-1 bg-green-600 text-sm md:text-sm text-xs font-medium text-white rounded hover:bg-green-700 border-2 border-green-600 flex items-center gap-2 md:gap-2 gap-1"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"/>
            </svg>
            {{ t('events.addEvent') }}
          </router-link>
          <router-link
            to="/events/cycles"
            class="flex items-center gap-2 md:gap-2 gap-1 text-sm md:text-sm text-xs text-blue-600 hover:text-blue-800 hover:underline"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z" clip-rule="evenodd"/>
            </svg>
            {{ t('events.eventCycles') }}
          </router-link>
          <button
            type="button"
            class="flex items-center gap-2 md:gap-2 gap-1 text-sm md:text-sm text-xs text-gray-600 hover:text-gray-800 hover:underline cursor-pointer"
            @click="showAttendanceReportModal = true"
          >
            <svg class="w-4 h-4 md:w-4 md:h-4 w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            {{ t('eventCycles.attendanceReport.buttonLabel') }}
          </button>
        </div>
      </div>

      <!-- Active Filters -->
      <div v-if="selectedCategories.length > 0" class="mb-4 flex flex-wrap gap-2">
        <div
          v-for="categoryUuid in selectedCategories"
          :key="categoryUuid"
          class="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded border border-blue-300"
        >
          <button @click="removeCategory(categoryUuid)" class="hover:text-blue-900">
            <svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"/>
            </svg>
          </button>
          <span>{{ t('events.category') }}: {{ getCategoryName(categoryUuid) }}</span>
        </div>
      </div>

      <!-- Error Message -->
      <div v-if="error" class="mb-4 p-4 bg-red-50 border-2 border-red-300 text-red-700 rounded">
        {{ error }}
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="text-center py-8">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
        <p class="mt-2 text-gray-600">{{ t('events.loadingEvents') }}</p>
      </div>

      <!-- Events Table (Desktop) -->
      <div v-if="!loading" class="hidden md:block bg-white border-2 border-gray-300 rounded shadow-sm overflow-y-visible" style="overflow-x: clip;">
        <table class="w-full text-sm">
          <thead class="bg-gray-100 border-b-2 border-gray-300">
            <tr>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('events.event') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('events.place') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('events.categories') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('events.day') }}</th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700"><span class="lg:hidden">{{ t('events.date') }}</span><span class="hidden lg:inline">{{ t('events.start') }}</span></th>
              <th class="px-4 py-3 text-left font-semibold text-gray-700 hidden lg:table-cell">{{ t('events.end') }}</th>
              <th v-if="currentRole !== 'PLAYER'" class="px-4 py-3 text-left font-semibold text-gray-700">{{ t('events.attendance') }}</th>
              <th class="px-4 py-3 text-right font-semibold text-gray-700 sticky right-0 bg-gray-100"></th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200">
            <tr
              v-for="(event, index) in events"
              :key="index"
              class="hover:bg-gray-50"
              :style="isEventPast(event) ? 'box-shadow: inset 4px 0 0 #9ca3af' : 'box-shadow: inset 4px 0 0 #3b82f6'"
            >
              <td class="px-4 py-3 text-gray-900 font-medium">
                <router-link
                  v-if="canShow"
                  :to="`/events/${event.uuid}?date=${event.start?.split(' ')[0]}`"
                  class="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {{ event.title }}
                </router-link>
                <span v-else>{{ event.title }}</span>
                <div class="lg:hidden text-xs text-gray-500 mt-0.5">{{ event.place }}</div>
              </td>
              <td class="px-4 py-3 text-gray-700 hidden lg:table-cell">{{ event.place }}</td>
              <td class="px-4 py-3 hidden lg:table-cell">
                <div v-if="event.categories && event.categories.length > 0" class="flex flex-wrap gap-1">
                  <template v-for="category in event.categories" :key="category.uuid">
                    <router-link
                      v-if="isCategoryAccessible(category.uuid)"
                      :to="`/categories/${category.uuid}`"
                      class="inline-block px-2 py-0.5 bg-gray-300 text-gray-900 text-[10px] rounded cursor-pointer hover:bg-gray-500 transition-colors"
                    >
                      {{ category.name }}
                    </router-link>
                    <span
                      v-else
                      class="inline-block px-2 py-0.5 bg-gray-300 text-gray-500 text-[10px] rounded opacity-60"
                    >
                      {{ category.name }}
                    </span>
                  </template>
                </div>
                <span v-else class="text-gray-500 text-[10px]">-</span>
              </td>
              <td class="px-4 py-3 text-gray-700">{{ event.day }}</td>
              <td class="px-4 py-3 text-gray-700">
                {{ event.start }}
                <div class="lg:hidden text-xs text-gray-500 mt-0.5">{{ event.end }}</div>
              </td>
              <td class="px-4 py-3 text-gray-700 hidden lg:table-cell">{{ event.end }}</td>
              <td v-if="currentRole !== 'PLAYER'" class="px-4 py-3">
                <span v-if="event.has_attendance" class="text-gray-700">{{ event.attendance_present_count }}/{{ event.attendance_all_count }}</span>
                <span v-else class="inline-block px-2 py-1 bg-red-50 text-red-700 text-xs font-medium rounded border border-red-300">{{ t('events.noAttendance') }}</span>
              </td>
              <td class="px-4 py-3 sticky right-0 bg-inherit text-right" style="position: relative;">
                <div class="flex items-center gap-1 justify-end">
                  <button
                    v-if="canSeeActions"
                    @click="toggleDropdown(`desktop-${index}`, $event)"
                    class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                  >
                    {{ t('common.actions') }}
                  </button>
                  <div
                    v-if="canSeeActions"
                    :class="[
                      'custom-dropdown',
                      'dropdown-bottom',
                      { 'show': openDropdownId === `desktop-${index}` }
                    ]"
                  >
                    <router-link v-if="canShow" :to="`/events/${event.uuid}?date=${event.start?.split(' ')[0]}`">{{ t('common.view') }}</router-link>
                    <router-link v-if="canUpdate" :to="`/events/${event.uuid}/edit?date=${event.start?.split(' ')[0]}`">{{ t('common.edit') }}</router-link>
                    <router-link v-if="canAttendanceStore && !event.has_attendance" :to="`/events/${event.uuid}/attendance/add?date=${event.start?.split(' ')[0]}`">{{ t('events.addAttendance') }}</router-link>
                    <router-link v-if="canAttendanceUpdate && event.has_attendance" :to="`/events/${event.uuid}/attendance/edit?date=${event.start?.split(' ')[0]}`">{{ t('events.editAttendance') }}</router-link>
                    <button v-if="canDelete" class="delete-action" @click="openDeleteModalFn(event)">{{ t('common.delete') }}</button>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Events Accordion (Mobile) -->
      <div v-if="!loading" class="md:hidden space-y-2">
        <div
          v-for="(event, index) in events"
          :key="index"
          :class="[
            'bg-white border-2 border-gray-300 rounded shadow-sm border-l-4',
            isEventPast(event) ? 'border-l-gray-400' : 'border-l-blue-500'
          ]"
        >
          <!-- Accordion Header -->
          <div class="flex items-center justify-between p-3 cursor-pointer" @click="toggleAccordion(index)">
            <!-- Event info -->
            <div class="flex-1 min-w-0">
              <div class="text-sm font-medium truncate">
                <router-link
                  v-if="canShow"
                  :to="`/events/${event.uuid}?date=${event.start?.split(' ')[0]}`"
                  class="text-blue-600 hover:text-blue-800 hover:underline"
                  @click.stop
                >
                  {{ event.title }}
                </router-link>
                <span v-else>{{ event.title }}</span>
              </div>
              <div class="text-xs text-gray-600 mt-1">
                <span class="font-medium">{{ getDayAbbreviation(event.day) }}</span>
                <span class="ml-2">{{ event.start }}</span>
                <span class="hidden min-[550px]:inline ml-1">- {{ event.end }}</span>
              </div>
            </div>

            <!-- Action buttons -->
            <div class="flex items-center gap-2 ml-2">
              <router-link
                v-if="canAttendanceStore"
                :to="`/events/${event.uuid}/attendance/add?date=${event.start?.split(' ')[0]}`"
                @click.stop
                :class="[
                  'px-3 py-1.5 rounded hover:opacity-80 border flex items-center justify-center',
                  event.has_attendance ? 'bg-green-100 text-green-700 border-green-300' : 'bg-red-100 text-red-700 border-red-300'
                ]"
              >
                <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 2a1 1 0 000 2h2a1 1 0 100-2H9z"/>
                  <path fill-rule="evenodd" d="M4 5a2 2 0 012-2 3 3 0 003 3h2a3 3 0 003-3 2 2 0 012 2v11a2 2 0 01-2 2H6a2 2 0 01-2-2V5zm3 4a1 1 0 000 2h.01a1 1 0 100-2H7zm3 0a1 1 0 000 2h3a1 1 0 100-2h-3zm-3 4a1 1 0 100 2h.01a1 1 0 100-2H7zm3 0a1 1 0 100 2h3a1 1 0 100-2h-3z" clip-rule="evenodd"/>
                </svg>
              </router-link>
              <div v-if="canSeeActions" style="position: relative;">
                <button
                  @click="toggleDropdown(`mobile-${index}`, $event)"
                  class="px-3 py-1.5 bg-gray-100 text-xs font-medium text-gray-700 rounded hover:bg-gray-200 border border-gray-300"
                >
                  {{ t('common.actions') }}
                </button>
                <div
                  :class="['custom-dropdown', 'dropdown-bottom', { 'show': openDropdownId === `mobile-${index}` }]"
                >
                  <router-link v-if="canShow" :to="`/events/${event.uuid}?date=${event.start?.split(' ')[0]}`">{{ t('common.view') }}</router-link>
                  <router-link v-if="canUpdate" :to="`/events/${event.uuid}/edit?date=${event.start?.split(' ')[0]}`">{{ t('common.edit') }}</router-link>
                  <router-link v-if="canAttendanceStore && !event.has_attendance" :to="`/events/${event.uuid}/attendance/add?date=${event.start?.split(' ')[0]}`">{{ t('events.addAttendance') }}</router-link>
                  <router-link v-if="canAttendanceUpdate && event.has_attendance" :to="`/events/${event.uuid}/attendance/edit?date=${event.start?.split(' ')[0]}`">{{ t('events.editAttendance') }}</router-link>
                  <button v-if="canDelete" class="delete-action" @click="openDeleteModalFn(event)">{{ t('common.delete') }}</button>
                </div>
              </div>
              <svg
                :class="['w-5 h-5 text-gray-600 transition-transform', { 'rotate-180': openAccordionIds.has(index) }]"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path fill-rule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clip-rule="evenodd"/>
              </svg>
            </div>
          </div>
          <!-- Accordion Content -->
          <div v-show="openAccordionIds.has(index)" class="border-t-2 border-gray-300 p-3">
            <div class="space-y-2 text-xs">
              <div>
                <span class="font-semibold text-gray-700">{{ t('events.place') }}:</span>
                <span class="text-gray-600">{{ event.place }}</span>
              </div>
              <div v-if="currentRole !== 'PLAYER'">
                <span class="font-semibold text-gray-700">{{ t('events.attendance') }}:</span>
                <span v-if="event.has_attendance" class="text-gray-600">{{ event.attendance_present_count }}/{{ event.attendance_all_count }}</span>
                <span v-else class="inline-block px-2 py-1 bg-red-50 text-red-700 text-xs font-medium rounded border border-red-300">{{ t('events.noAttendance') }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!loading && totalEvents > 0" class="mt-4 flex flex-col lg:flex-row justify-between items-center gap-4">
        <div class="text-sm text-gray-700 w-full lg:w-auto text-center lg:text-left">
          {{ t('events.displaying') }} {{ fromItem }} - {{ toItem }} {{ t('events.of') }} {{ totalEvents }} {{ t('events.eventsWord') }}
        </div>
        <div class="flex flex-col md:flex-row items-center gap-4 w-full lg:w-auto">
          <div class="flex items-center gap-2 w-full md:w-auto justify-center md:justify-start">
            <label for="perPage" class="text-sm text-gray-700">{{ t('events.eventsPerPage') }}:</label>
            <select id="perPage" v-model.number="perPage" @change="changePerPage" class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white">
              <option :value="25">25</option>
              <option :value="50">50</option>
              <option :value="75">75</option>
              <option :value="100">100</option>
            </select>
          </div>
          <div class="flex items-center gap-1 w-full md:w-auto justify-center md:justify-start">
            <button
              @click="prevPage"
              :disabled="currentPage === 1"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &laquo;
            </button>
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="changePage(page)"
              :class="[
                'px-3 py-1 border-2 rounded text-sm',
                page === currentPage
                  ? 'border-gray-900 bg-gray-900 text-white font-medium'
                  : 'border-gray-300 bg-white hover:bg-gray-50'
              ]"
            >
              {{ page }}
            </button>
            <button
              @click="nextPage"
              :disabled="currentPage === lastPage"
              class="px-3 py-1 border-2 border-gray-300 rounded text-sm bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              &raquo;
            </button>
          </div>
        </div>
      </div>
    </div>
  </main>

  <!-- Delete Confirmation Modal -->
  <div
    v-if="showDeleteModal"
    class="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
    @click.self="closeDeleteModal"
  >
    <div class="bg-white rounded-lg shadow-xl max-w-md w-full">
      <div class="p-6">
        <h2 class="text-xl font-bold text-gray-900 mb-4">{{ t('events.deleteConfirmation') }}</h2>
        <p class="text-gray-700 mb-6">
          {{ t('events.deleteConfirmationMessage') }} "<span class="font-semibold">{{ eventToDelete?.title }}</span>"?
        </p>

        <div class="flex gap-3 justify-end">
          <button
            @click="closeDeleteModal"
            class="px-4 py-2 bg-gray-100 text-sm font-medium text-gray-700 rounded hover:bg-gray-200 border-2 border-gray-300"
            :disabled="deleting"
          >
            {{ t('common.cancel') }}
          </button>
          <button
            @click="handleDelete"
            class="px-4 py-2 bg-red-600 text-sm font-medium text-white rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
            :disabled="deleting"
          >
            {{ deleting ? t('events.deleting') : t('common.delete') }}
          </button>
        </div>
      </div>
    </div>
  </div>

  <AttendanceReportModal
    :visible="showAttendanceReportModal"
    @close="showAttendanceReportModal = false"
  />
</template>

<style scoped>
</style>
